/**
 * UserManagementPanel — NTN-staff-only user CRUD within a client account.
 *
 * Drops into ClientDetailDialog (and could mount standalone). Renders the
 * 6-column directory required by Section 5 of the admin spec:
 *   First | Last | Email | Role | Status | Last Login
 * Each row exposes Edit / Disable-Enable / Reset Password / Remove actions.
 *
 * Wires to the canonical staff endpoints:
 *   POST   /admin/clients/{cid}/users   (routes/admin_clients_v2.py — GEN-05 canonical)
 *   PATCH  /admin/users/{id}            (routes/admin.py)
 *   DELETE /admin/users/{id}            (routes/admin.py)
 *   POST   /admin/users/{id}/reset-password (routes/admin.py)
 */
import { useEffect, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, KeyRound, Power, PowerOff, Trash2, Pencil, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dateFormat";

const ROLE_LABEL = {
  admin: "Account Administrator",
  member: "Standard User",
  read_only: "Read Only",
};

function statusOf(u) {
  if (u.disabled_at) return { label: "Disabled", tone: "danger" };
  if (u.deleted_at)  return { label: "Removed",  tone: "muted" };
  if (!u.last_login_at) return { label: "Invited", tone: "warn" };
  return { label: "Active", tone: "ok" };
}

const TONE_CLS = {
  ok:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  warn:   "bg-amber-50 text-amber-700 border-amber-200",
  danger: "bg-red-50 text-red-700 border-red-200",
  muted:  "bg-slate-100 text-slate-600 border-slate-200",
};

export default function UserManagementPanel({ clientId, clientName, onChanged }) {
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/admin/clients/${clientId}/users`);
      setUsers(Array.isArray(data) ? data : (data.users || []));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load users");
    }
  };
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line

  return (
    <div data-testid="user-management-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">
          Users ({users.length})
        </Label>
        <AddUserDialog open={addOpen} onOpenChange={setAddOpen} clientId={clientId}
                       clientName={clientName} onCreated={() => { load(); onChanged?.(); }}
                       onTempPassword={setResetResult} />
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] tracking-widest uppercase font-semibold text-slate-500">
          <div className="col-span-2">First</div>
          <div className="col-span-2">Last</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Last login / Actions</div>
        </div>
        <ul className="divide-y divide-slate-200 max-h-[420px] overflow-y-auto">
          {users.length === 0 ? (
            <li className="p-4 text-sm text-slate-500 text-center">No users yet. Click "Add user" to invite one.</li>
          ) : users.map((u) => (
            <UserRow key={u.id} u={u} reload={() => { load(); onChanged?.(); }}
                     onTempPassword={setResetResult} />
          ))}
        </ul>
      </div>

      <TempPasswordDialog result={resetResult} onClose={() => setResetResult(null)} />
    </div>
  );
}

function UserRow({ u, reload, onTempPassword }) {
  const st = statusOf(u);
  const [editOpen, setEditOpen] = useState(false);

  const toggleDisabled = async () => {
    try {
      await apiClient.patch(`/admin/users/${u.id}`, { disabled: !u.disabled_at });
      toast.success(u.disabled_at ? "User enabled" : "User disabled");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const remove = async () => {
    try {
      await apiClient.delete(`/admin/users/${u.id}`);
      toast.success("User removed");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Remove failed"); }
  };

  const resetPassword = async (method) => {
    try {
      const { data } = await apiClient.post(`/admin/users/${u.id}/reset-password`, { method });
      if (data.temp_password) onTempPassword({ email: u.email, ...data });
      else toast.success("Reset link emailed");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };

  return (
    <li className="px-3 py-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center text-sm" data-testid={`user-row-${u.id}`}>
      <div className="md:col-span-2 truncate text-slate-900">{u.first_name || u.name?.split(" ")[0] || "—"}</div>
      <div className="md:col-span-2 truncate text-slate-900">{u.last_name || u.name?.split(" ").slice(1).join(" ") || "—"}</div>
      <div className="md:col-span-3 truncate font-mono text-xs text-slate-700">{u.email}</div>
      <div className="md:col-span-2">
        <Badge variant="outline" className="text-[10px] font-mono uppercase border-slate-200">
          {ROLE_LABEL[u.account_role] || u.account_role}
        </Badge>
      </div>
      <div className="md:col-span-1">
        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${TONE_CLS[st.tone]}`} data-testid={`user-status-${u.id}`}>
          {st.label}
        </Badge>
      </div>
      <div className="md:col-span-2 flex items-center justify-end gap-1 flex-wrap">
        <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-blue-50 text-blue-700 border-blue-200 mr-1" title="Email verification code required at every sign-in" data-testid={`user-mfa-${u.id}`}>
          MFA: Email
        </Badge>
        <div className="text-[10px] font-mono text-slate-500 mr-1">{fmtDate(u.last_login_at)}</div>
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Edit user" data-testid={`user-edit-btn-${u.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </DialogTrigger>
          <EditUserDialogContent u={u} onSaved={() => { setEditOpen(false); reload(); }} />
        </Dialog>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Reset password" data-testid={`user-reset-btn-${u.id}`}>
              <KeyRound className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset password for {u.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                Choose how to deliver the new password. Active sessions will be revoked.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => resetPassword("temp_password")} className="bg-slate-900" data-testid={`user-reset-temp-${u.id}`}>
                Generate temp password
              </AlertDialogAction>
              <AlertDialogAction onClick={() => resetPassword("email_link")} className="bg-blue-600 hover:bg-blue-700" data-testid={`user-reset-email-${u.id}`}>
                Email reset link
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                title={u.disabled_at ? "Enable user" : "Disable user"}
                onClick={toggleDisabled}
                data-testid={`user-disable-btn-${u.id}`}>
          {u.disabled_at ? <Power className="h-3.5 w-3.5 text-emerald-600" /> : <PowerOff className="h-3.5 w-3.5 text-amber-600" />}
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" title="Remove user" data-testid={`user-remove-btn-${u.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {u.email}?</AlertDialogTitle>
              <AlertDialogDescription>
                The user is soft-removed; sessions are revoked and they can no longer sign in. You can re-invite them with the same email later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid={`user-remove-confirm-${u.id}`}>
                Remove user
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}

function EditUserDialogContent({ u, onSaved }) {
  const [first, setFirst] = useState(u.first_name || "");
  const [last, setLast] = useState(u.last_name || "");
  const [phone, setPhone] = useState(u.phone || "");
  const [name, setName] = useState(u.name || "");
  const [role, setRole] = useState(u.account_role || "member");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await apiClient.patch(`/admin/users/${u.id}`, {
        name, first_name: first, last_name: last, phone, account_role: role,
      });
      toast.success("User updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Edit user · {u.email}</DialogTitle>
        <DialogDescription>Updates contact info and role. Email and login credentials are unchanged.</DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name"><Input value={first} onChange={(e) => setFirst(e.target.value)} className="h-10 rounded-md" data-testid="edit-user-first" /></Field>
        <Field label="Last name"><Input value={last} onChange={(e) => setLast(e.target.value)} className="h-10 rounded-md" data-testid="edit-user-last" /></Field>
        <Field label="Display name" className="col-span-2"><Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-md" data-testid="edit-user-name" /></Field>
        <Field label="Phone"><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-10 rounded-md" data-testid="edit-user-phone" /></Field>
        <Field label="Role">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-10 rounded-md" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="admin">Account Administrator</SelectItem>
              <SelectItem value="member">Standard User</SelectItem>
              <SelectItem value="read_only">Read Only</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>
      <DialogFooter>
        <Button onClick={save} disabled={busy} className="bg-slate-900" data-testid="edit-user-save">{busy ? "Saving…" : "Save changes"}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function AddUserDialog({ open, onOpenChange, clientId, clientName, onCreated, onTempPassword }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", account_role: "member", invite_method: "email_link" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try {
      const name = `${form.first_name} ${form.last_name}`.trim();
      const { data } = await apiClient.post(`/admin/clients/${clientId}/users`, {
        ...form, name,
      });
      onOpenChange(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", account_role: "member", invite_method: "email_link" });
      if (data.temp_password) {
        onTempPassword({ email: data.user.email, ...data });
      } else {
        toast.success("Invitation email sent");
      }
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add user"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 rounded-md h-8" data-testid="add-user-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add user to {clientName}</DialogTitle>
          <DialogDescription>
            Choose how to deliver credentials. Email invitations send a "Set your password" link; temp-password mode hands you a one-time string to share securely.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="h-10 rounded-md" data-testid="add-user-first" /></Field>
          <Field label="Last name"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-10 rounded-md" data-testid="add-user-last" /></Field>
          <Field label="Email" className="col-span-2"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-10 rounded-md" data-testid="add-user-email" /></Field>
          <Field label="Phone (optional)"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-10 rounded-md" data-testid="add-user-phone" /></Field>
          <Field label="Role">
            <Select value={form.account_role} onValueChange={(v) => set("account_role", v)}>
              <SelectTrigger className="h-10 rounded-md" data-testid="add-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Account Administrator</SelectItem>
                <SelectItem value="member">Standard User</SelectItem>
                <SelectItem value="read_only">Read Only (reserved)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Delivery" className="col-span-2">
            <Select value={form.invite_method} onValueChange={(v) => set("invite_method", v)}>
              <SelectTrigger className="h-10 rounded-md" data-testid="add-user-invite-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email_link">Send invitation email (preferred)</SelectItem>
                <SelectItem value="temp_password">Generate temporary password</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !form.first_name || !form.last_name || !form.email}
                  className="bg-slate-900" data-testid="add-user-submit">
            {busy ? "Adding…" : "Add user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TempPasswordDialog({ result, onClose }) {
  if (!result) return null;
  const copy = () => {
    navigator.clipboard.writeText(result.temp_password || "");
    toast.success("Copied to clipboard");
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Temporary password ready</DialogTitle>
          <DialogDescription>Share this with <strong>{result.email}</strong> over a trusted channel. It won't be shown again.</DialogDescription>
        </DialogHeader>
        <div className="bg-slate-50 ring-1 ring-slate-200 rounded-md px-4 py-3 font-mono text-base text-slate-900 flex items-center justify-between gap-2">
          <span className="break-all" data-testid="temp-password-value">{result.temp_password}</span>
          <Button size="sm" variant="ghost" onClick={copy} data-testid="temp-password-copy"><Copy className="h-4 w-4" /></Button>
        </div>
        <DialogFooter><Button onClick={onClose}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
